import {
  DateRangeFieldsRestriction,
  FormFieldRestrictionDTO,
  FormItemResponseDTO,
  FormItemTypeDTO,
} from '@/api/base/DTOs';
import {
  FormField,
  FormValidation,
  IFormBase,
  IFormCheckbox,
  IFormDate,
  IFormInput,
  IFormRadioGroup,
  IFormSelect,
} from '@/forms/typings';
import { createFormFieldValidation } from '@/forms/utils/createFormFieldValidation';

/**
 * Transforms a FormItemResponseDTO into a FormField
 */
export type FormItemAdapterFunction = (
  item: FormItemResponseDTO,
  parentFieldId?: IFormBase['fieldId'],
) => FormField;

/** Which side of a date-range pair the restriction applies to. */
export enum DateRangeFieldType {
  From = 'From',
  To = 'To',
}

type FormValidationUnion =
  | IFormInput['validations']
  | IFormRadioGroup['validations']
  | IFormSelect['validations']
  | IFormCheckbox['validations']
  | IFormDate['validations'];

export type FormFieldRestrictionToValidationsFn = (
  formFieldRestriction?: FormFieldRestrictionDTO,
) => FormValidationUnion;

export type DateRangeRestrictionToValidationsFn = (
  fieldType: DateRangeFieldType,
  dateRangeFieldsRestrictionResponse?: DateRangeFieldsRestriction,
) => IFormDate['validations'];

function adaptFormFieldRestrictionResponseToFormValidationImpl(
  formFieldRestriction?: FormFieldRestrictionDTO,
): FormValidationUnion {
  if (!formFieldRestriction) return [] as FormValidationUnion;

  const restrictionToValidation = {
    isRequired: FormValidation.Required,
    email: FormValidation.Email,
    url: FormValidation.Url,
    minValue: FormValidation.MinValue,
    maxValue: FormValidation.MaxValue,
    minLength: FormValidation.MinLength,
    maxLength: FormValidation.MaxLength,
    regex: FormValidation.Regex,
  } as const;

  return Object.entries(formFieldRestriction)
    .filter(([key, value]) => value && key in restrictionToValidation)
    .map(([key, value]) => {
      const validationType =
        restrictionToValidation[key as keyof typeof restrictionToValidation];
      const validationValue = ['isRequired', 'email', 'url'].includes(key)
        ? undefined
        : (value as string | number);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (createFormFieldValidation[validationType] as any)(
        validationValue,
      );
    }) as FormValidationUnion;
}

function adaptDateRangeFieldsRestrictionResponseToFormValidationImpl(
  fieldType: DateRangeFieldType,
  dateRangeFieldsRestrictionResponse?: DateRangeFieldsRestriction,
): IFormDate['validations'] {
  const validations = [];

  switch (fieldType) {
    case DateRangeFieldType.From:
      if (dateRangeFieldsRestrictionResponse?.isFromRequired) {
        validations.push({ type: FormValidation.Required });
      }
      if (dateRangeFieldsRestrictionResponse?.minValue) {
        validations.push({
          type: FormValidation.MinValue,
          value: dateRangeFieldsRestrictionResponse.minValue,
        });
      }
      break;

    case DateRangeFieldType.To:
      if (dateRangeFieldsRestrictionResponse?.isToRequired) {
        validations.push({ type: FormValidation.Required });
      }
      if (dateRangeFieldsRestrictionResponse?.maxValue) {
        validations.push({
          type: FormValidation.MaxValue,
          value: dateRangeFieldsRestrictionResponse.maxValue,
        });
      }
      break;
  }

  return validations as IFormDate['validations'];
}

/**
 * **Registry 1 (API → frontend model)**: `FormItemTypeDTO` → `FormField` plus
 * restriction DTOs → validation rule lists. Registry 2 is `fieldModuleRegistry` in `src/forms/field-modules/registry/`.
 */
export class FormApiAdapterRegistry {
  private itemAdapters = new Map<FormItemTypeDTO, FormItemAdapterFunction>();

  private genericRestrictionAdapter: FormFieldRestrictionToValidationsFn =
    adaptFormFieldRestrictionResponseToFormValidationImpl;

  private dateRangeRestrictionAdapter: DateRangeRestrictionToValidationsFn =
    adaptDateRangeFieldsRestrictionResponseToFormValidationImpl;

  // --- Form items (DTO → FormField) ---

  register(
    type: FormItemTypeDTO,
    adapter: FormItemAdapterFunction,
  ): FormApiAdapterRegistry {
    this.itemAdapters.set(type, adapter);
    return this;
  }

  getAdapter(type: FormItemTypeDTO): FormItemAdapterFunction | undefined {
    return this.itemAdapters.get(type);
  }

  hasAdapter(type: FormItemTypeDTO): boolean {
    return this.itemAdapters.has(type);
  }

  adapt(
    item: FormItemResponseDTO,
    parentFieldId?: IFormBase['fieldId'],
  ): FormField {
    const adapter = this.itemAdapters.get(item.formItemType);

    if (!adapter) {
      throw new Error(
        `No adapter registered for form item type: ${item.formItemType}. ` +
          `Available types: ${Array.from(this.itemAdapters.keys()).join(', ')}`,
      );
    }

    return adapter(item, parentFieldId);
  }

  getRegisteredTypes(): FormItemTypeDTO[] {
    return Array.from(this.itemAdapters.keys());
  }

  // --- Restrictions (API DTO → validation rules) ---

  registerGeneric(adapter: FormFieldRestrictionToValidationsFn): this {
    this.genericRestrictionAdapter = adapter;
    return this;
  }

  registerDateRange(adapter: DateRangeRestrictionToValidationsFn): this {
    this.dateRangeRestrictionAdapter = adapter;
    return this;
  }

  adaptGeneric(formFieldRestriction?: FormFieldRestrictionDTO): FormValidationUnion {
    return this.genericRestrictionAdapter(formFieldRestriction);
  }

  adaptDateRange(
    fieldType: DateRangeFieldType,
    dateRangeFieldsRestrictionResponse?: DateRangeFieldsRestriction,
  ): IFormDate['validations'] {
    return this.dateRangeRestrictionAdapter(
      fieldType,
      dateRangeFieldsRestrictionResponse,
    );
  }

  /**
   * Clears item adapters and resets restriction adapters to defaults (tests).
   */
  clear(): void {
    this.itemAdapters.clear();
    this.genericRestrictionAdapter =
      adaptFormFieldRestrictionResponseToFormValidationImpl;
    this.dateRangeRestrictionAdapter =
      adaptDateRangeFieldsRestrictionResponseToFormValidationImpl;
  }
}

export const formApiAdapterRegistry = new FormApiAdapterRegistry();
